import { useState } from 'react';
import {
  AutocompleteInput,
  BooleanInput,
  DateInput,
  Form,
  required,
  SelectInput,
  TextInput,
  useNotify,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import PropTypes from 'prop-types';

import {
  dateIsAfter,
  getDateAsFormattedUTC,
  getTimeDifferenceFromCurrentDate,
  validateDateYYYYMMDD,
} from '../../../../shared/utils';
import { addIncidentModalStyle } from './add-incident-modal-style';

AddIncidentModal.propTypes = {
  onAddIncident: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  disableButton: PropTypes.bool,
  coverageStartDate: PropTypes.string,
  accidentCoverageStartDate: PropTypes.string,
  wellnessCoverageStartDate: PropTypes.string,
  subDiagnosisData: PropTypes.array,
  isSaving: PropTypes.bool,
  petId: PropTypes.string,
};

export default function AddIncidentModal(props) {
  const {
    onClose,
    open,
    onAddIncident,
    disableButton,
    coverageStartDate,
    accidentCoverageStartDate,
    wellnessCoverageStartDate,
    subDiagnosisData = [],
    isSaving,
    petId,
  } = props;

  const notify = useNotify();
  const [diagnosis, setDiagnosis] = useState('');
  const [diagnosisId, setDiagnosisId] = useState('');
  const [incidentType, setIncidentType] = useState('');
  const [symptomOnset, setSymptomOnset] = useState('');
  const [disableMarkAsCured, setDisableMarkAsCured] = useState(false);
  const [curability, setCurability] = useState(false);
  const [symptomSelected, setSymptomSelected] = useState(0);
  const classes = addIncidentModalStyle();

  function handleAddLineItemClick(props) {
    const { incident_type, symptom_date, latest_occurrence_date, subdiagnosis } =
      props;

    if (!incident_type) {
      notify('Please choose an incident type', { type: 'warning' });
    } else if (!symptom_date) {
      notify('Please select a date for symptom onset', { type: 'warning' });
    } else if (!diagnosisId) {
      notify('Please choose a diagnosis', { type: 'warning' });
    } else if (!subdiagnosis) {
      notify('Please choose a subdiagnosis', { type: 'warning' });
    } else if (symptom_date > latest_occurrence_date) {
      notify("Latest Occurrence Date can't predate Symptom Onset date", {
        type: 'warning',
      });
    } else {
      onAddIncident({ ...props, pet_id: petId, diagnosis: diagnosisId });
    }
  }

  function handleOnClose(props) {
    onClose(props);
  }

  function handleOnSymptomBlur(symptomOnsetDate, setValue, trigger) {
    setSymptomOnset(symptomOnsetDate);

    const coverageStart =
      incidentType == 'accident'
        ? accidentCoverageStartDate
        : incidentType == 'illness'
          ? coverageStartDate
          : wellnessCoverageStartDate;
    if (incidentType && dateIsAfter(coverageStart, symptomOnsetDate)) {
      setValue('pre_existing_condition', true);
    } else {
      setValue('pre_existing_condition', false);
    }

    if (validateDateYYYYMMDD(symptomOnsetDate) && symptomSelected == 0) {
      setValue('latest_occurrence_date', symptomOnsetDate);
      setSymptomSelected(symptomSelected + 1);
      trigger('latest_occurrence_date');
    }
  }

  function handleIncidentTypeChange(incidentType, setValue) {
    const coverageStart =
      incidentType == 'accident'
        ? accidentCoverageStartDate
        : incidentType == 'illness'
          ? coverageStartDate
          : wellnessCoverageStartDate;
    if (symptomOnset && new Date(symptomOnset) < new Date(coverageStart)) {
      setValue('pre_existing_condition', true);
    }
    setIncidentType(incidentType);
  }

  function handleOnChangeCurability(curability, setValue) {
    if (curability === 'incurable') {
      setValue('mark_as_cured', false);
      setDisableMarkAsCured(true);
    } else {
      setDisableMarkAsCured(false);
    }
    setCurability(curability);
  }

  function validateLatestOccurrenceDate(value) {
    if (!value) {
      return 'Required';
    }

    if (dateIsAfter(symptomOnset, value)) {
      return "Latest Occurrence Date can't predate Symptom Onset date";
    }

    if (dateIsAfter(value, Date.now())) {
      return 'Latest Occurrence be later than today';
    }

    return undefined;
  }

  function validateSymptomOnsetDate(value) {
    if (!value) {
      return 'Required';
    }
    if (dateIsAfter(value, Date.now())) {
      return 'Latest Occurrence be later than today';
    }
    return undefined;
  }

  function LatestOccurrenceDateInput() {
    return (
      <DateInput
        fullWidth
        variant='outlined'
        label='Latest Occurrence'
        source='latest_occurrence_date'
        validate={value => validateLatestOccurrenceDate(value)}
        className={classes.datePickerMargin}
      />
    );
  }

  function SymptomDateInput() {
    const { setValue, trigger } = useFormContext();
    return (
      <DateInput
        fullWidth
        variant='outlined'
        label='Symptom Onset'
        source='symptom_date'
        className={classes.datePickerMargin}
        onBlur={e => handleOnSymptomBlur(e.target.value, setValue, trigger)}
        validate={value => validateSymptomOnsetDate(value)}
      />
    );
  }

  function IncidentTypeSelectInput() {
    const { setValue, getFieldState } = useFormContext();
    const { error: incidentTypeError } = getFieldState('incident_type');

    return (
      <SelectInput
        className={classes.incidentTypeSelect}
        sx={{
          backgroundColor: incidentType ? '#002984' : 'white',
          border: incidentTypeError
            ? '1px solid red'
            : incidentType
              ? 'none'
              : '1px solid black',
          '& .MuiFormLabel-root': {
            display: incidentType ? 'none' : 'inherit',
          },

          '& .MuiSvgIcon-root': {
            color: incidentTypeError ? 'red' : incidentType ? 'white' : 'black',
          },
        }}
        onChange={e => handleIncidentTypeChange(e.target.value, setValue)}
        fullWidth
        source='incident_type'
        label='TYPE'
        choices={[
          { id: 'accident', name: 'ACCIDENT' },
          { id: 'illness', name: 'ILLNESS' },
          { id: 'wellness', name: 'WELLNESS' },
        ]}
        validate={required()}
        inputProps={{ 'data-testid': 'incident' }}
        InputLabelProps={{
          shrink: false,
          'aria-label': 'Without label',
        }}
        variant='standard'
      />
    );
  }

  function CurabilitySelectInput() {
    const { setValue, getFieldState } = useFormContext();
    const { error: curabilityError } = getFieldState('is_curable');

    return (
      <SelectInput
        className={classes.curabilitySelect}
        sx={{
          backgroundColor: curability ? '#9e9e9e' : 'inherit',
          border: curabilityError
            ? '1px solid red'
            : curability
              ? 'none'
              : '1px solid black',
          '& .MuiFormLabel-root': {
            display: curability ? 'none' : 'inherit',
          },
          '& .MuiSvgIcon-root': {
            color: curabilityError ? 'red' : curability ? 'white' : 'black',
          },
        }}
        onChange={event => handleOnChangeCurability(event.target.value, setValue)}
        fullWidth
        inputProps={{
          'data-testid': 'is_curable',
          'aria-label': 'Without label',
        }}
        source='is_curable'
        label='CURABILITY'
        validate={required()}
        choices={[
          { id: 'curable', name: 'CURABLE' },
          { id: 'incurable', name: 'INCURABLE' },
          { id: 'unknown', name: 'UNKNOWN' },
        ]}
        InputLabelProps={{ shrink: false }}
        variant='standard'
      />
    );
  }

  function PreExistingInput() {
    const coverageStart =
      incidentType == 'accident'
        ? accidentCoverageStartDate
        : incidentType == 'illness'
          ? coverageStartDate
          : wellnessCoverageStartDate;

    return (
      <BooleanInput
        label={<Typography variant='subtitle2'>Mark as pre-existing</Typography>}
        source='pre_existing_condition'
        fullWidth
        helperText={`Cov. start: ${
          incidentType ? getDateAsFormattedUTC(coverageStart, 'MM/DD/YYYY') : ''
        }`}
        className={classes.preExistingCheck}
        size='small'
      />
    );
  }

  const diagnosisChoices = subDiagnosisData.map(diagnosisValue => {
    return {
      id: diagnosisValue.id,
      name: diagnosisValue.name,
      diagnosisName: diagnosisValue.diagnosis_name.toUpperCase(),
      diagnosisId: diagnosisValue.diagnosis_id,
    };
  });

  function AlertDaysSinceLatestOccurrence() {
    const latest_occurrence_date = useWatch({ name: 'latest_occurrence_date' });
    const is_curable = useWatch({ name: 'is_curable' });
    const cured = useWatch({ name: 'mark_as_cured' });
    if (is_curable === 'curable' && cured == false && latest_occurrence_date)
      return (
        <Alert
          icon={<AccessTimeIcon fontSize='inherit' />}
          className={classes.daysPastAlert}
        >
          <span style={{ color: '#373F51', fontWeight: '600' }}>
            {getTimeDifferenceFromCurrentDate(latest_occurrence_date, 'days')} days
            past since latest occurrence
          </span>
        </Alert>
      );
  }

  return (
    <Dialog open={open} onClose={handleOnClose} className={classes.dialogContainer}>
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: theme => Math.max.apply(Math, Object.values(theme.zIndex)) + 1,
        }}
        open={isSaving}
      >
        <CircularProgress color='inherit' />
      </Backdrop>
      <Form onSubmit={handleAddLineItemClick}>
        <DialogTitle variant='h6' color='primary'>
          ADD NEW INCIDENT
        </DialogTitle>

        <DialogContent className={classes.modalContent}>
          <div style={{ width: '371px', marginRight: '20px' }}>
            <AutocompleteInput
              fullWidth
              className={classes.autoComplete}
              onChange={(event, value) => {
                setDiagnosis(value.diagnosisName);
                setDiagnosisId(value.diagnosisId);
              }}
              choices={diagnosisChoices.sort((a, b) =>
                a.diagnosisName.localeCompare(b.diagnosisName)
              )}
              groupBy={subDiagnosisData => subDiagnosisData.diagnosisName}
              source='subdiagnosis'
              variant='outlined'
              size={'big'}
              optionText='name'
              label='Diagnosis'
              validate={required()}
              helperText={
                diagnosis ? (
                  <>
                    <span>Category </span>
                    <span style={{ color: '#2196F3', textTransform: 'capitalize' }}>
                      {diagnosis.toLowerCase()}
                    </span>
                  </>
                ) : null
              }
            />
            <div className={classes.diagnosisDiv}>
              <IncidentTypeSelectInput />
              <CurabilitySelectInput />
            </div>
            <div className={classes.datePickerDiv}>
              <SymptomDateInput />
              <LatestOccurrenceDateInput />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <PreExistingInput />
              <BooleanInput
                label={
                  <Typography variant='subtitle2'>Mark as cured date</Typography>
                }
                source='mark_as_cured'
                fullWidth
                disabled={disableMarkAsCured}
                className={classes.markAsCuredCheck}
                size='small'
              />
            </div>
            <AlertDaysSinceLatestOccurrence />
          </div>

          <div
            style={{
              width: '565px',
              height: '100%',
            }}
          >
            <TextInput
              multiline
              label='Medical Record Reference/Notes'
              source='notes'
              minRows={2}
              fullWidth
              variant={'filled'}
              size={'medium'}
              className={classes.notesTextInput}
            />
          </div>
        </DialogContent>
        <DialogActions style={{ justifyContent: 'space-between' }}>
          <Button
            onClick={onClose}
            variant='outlined'
            className={classes.cancelButton}
          >
            CANCEL
          </Button>
          <Button
            disabled={disableButton}
            type='submit'
            color='primary'
            variant='contained'
            className={classes.addButton}
          >
            ADD
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
}
